'use client'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import type { CompanyPartner } from '@/payload-types'
import { getCompanyPartnersAction } from '@/utilities/cms-actions'

export interface SelectedCompany {
  id: number
  name: string
  sfId: string | null
}

interface CompanyAutocompleteProps {
  companyPartners?: CompanyPartner[]
  value: string
  onChange: (value: string) => void
  onSelect: (company: SelectedCompany | null) => void
  id?: string
  isConsultation?: boolean
  placeholder?: string
  ref?: React.RefObject<HTMLInputElement> | null
}

export default function CompanyAutocomplete({
  companyPartners,
  value,
  onChange,
  onSelect,
  isConsultation = false,
  id = 'referringCompanyName',
  ref = null,
  placeholder = 'ABC Corporation',
}: CompanyAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [fetchedPartners, setFetchedPartners] = useState<CompanyPartner[]>([])
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (companyPartners) return
    getCompanyPartnersAction()
      .then(setFetchedPartners)
      .catch(() => {})
  }, [companyPartners])

  const partners = companyPartners ?? fetchedPartners

  const matches = useMemo(() => {
    const query = value.trim().toLowerCase()
    if (!query) return partners
    return partners.filter((partner) => partner.name?.toLowerCase().includes(query))
  }, [partners, value])

  useEffect(() => {
    setHighlightedIndex(0)
  }, [value])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const selectPartner = (partner: CompanyPartner) => {
    onChange(partner.name || '')
    onSelect({ id: partner.id, name: partner.name || '', sfId: partner['Sf Id'] ?? null })
    setIsOpen(false)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value)
    onSelect(null)
    setIsOpen(true)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || matches.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((prev) => (prev + 1) % matches.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((prev) => (prev - 1 + matches.length) % matches.length)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      selectPartner(matches[highlightedIndex])
    } else if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }

  placeholder = isConsultation ? 'Referring company name' : placeholder

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        id={id}
        name={id}
        autoComplete="off"
        value={value}
        onChange={handleChange}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        ref={ref}
        className={` ${isConsultation ? 'w-full text-lg text-gray-900 placeholder:text-gray-400 py-3 border-0 border-b-2 bg-transparent border-gray-300 focus:border-brand-blue focus:outline-none' : 'w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent'}`}
      />
      <AnimatePresence>
        {isOpen && matches.length > 0 && (
          <motion.ul
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="absolute z-10 mt-2 w-full max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg "
          >
            {matches.map((partner, index) => (
              <li key={partner.id}>
                <button
                  type="button"
                  onClick={() => selectPartner(partner)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${index === highlightedIndex
                    ? ' text-brand-blue bg-brand-blue/10 '
                    : 'text-gray-700 '
                    }`}
                >
                  {partner.name}
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  )
}
